import {
  posterDate,
  posterScore,
} from "../../../shared/presentation/round-poster-score.js";
import type {
  RoundFeedCard,
  RoundFeedSide,
} from "../../../shared/contracts/round-feed.js";
import { ApprovedAvatar } from "../../shell/approved-primitives.js";
import { ArrowUpRight } from "../../shell/approved-icons.js";
import "../../styles/round-poster.css";
import "../../styles/played-to-handicap.css";
import {
  playedToHandicapLabel,
  playedToHandicapReason,
} from "../../../shared/presentation/played-to-handicap.js";

export function RoundPoster({
  card,
  onOpen,
}: {
  card: RoundFeedCard;
  onOpen(): void;
}) {
  const playerCount = new Set(card.sides.flatMap((side) => side.participantIds))
    .size;
  return (
    <button
      className="fr-round-poster"
      onClick={onOpen}
      aria-label={`Open result: ${card.sides.map((s) => s.name).join(" vs ")}. ${card.outcome}. ${card.courseName}`}
    >
      <span className="fr-poster-meta">
        <span>
          {card.environment === "simulator" ? "Simulator · " : ""}
          {card.format}
        </span>
        <time>{posterDate(card)}</time>
      </span>
      <span className="fr-poster-outcome">{card.outcome}</span>
      {card.stakeStory && (
        <span className="fr-poster-stake">{card.stakeStory}</span>
      )}
      <span className="fr-poster-matchup" data-kind={card.kind}>
        <span className="fr-poster-players">
          {card.sides.map((side, index) => (
            <PosterPlayer key={side.id} side={side} card={card} index={index} />
          ))}
        </span>
      </span>
      <span className="fr-poster-course">
        <span>
          {card.courseName} · {playerCount}{" "}
          {playerCount === 1 ? "player" : "players"}
        </span>
        <ArrowUpRight size={16} />
      </span>
    </button>
  );
}

function PosterPlayer({
  side,
  card,
  index,
}: {
  side: RoundFeedSide;
  card: RoundFeedCard;
  index: number;
}) {
  const won = card.winnerIds.length === 1 && card.winnerIds.includes(side.id);
  return (
    <span className="fr-poster-player" data-winner={won} data-side={index}>
      <span className="fr-poster-person">
        <ApprovedAvatar
          participantId={
            side.participantIds.length === 1 ? side.participantIds[0] : null
          }
          name={side.name}
          size={36}
        />
        <span>
          <strong>{side.name}</strong>
          {won && card.sides.length > 1 && <small>WINNER</small>}
          {!card.underReview &&
          side.playedToHandicap?.status === "before-pcc" ? (
            <span
              className="fr-poster-played-hc"
              title={playedToHandicapReason(side.playedToHandicap)}
            >
              {playedToHandicapLabel(side.playedToHandicap)} · Before PCC
            </span>
          ) : null}
        </span>
      </span>
      <PosterScore card={card} side={side} />
    </span>
  );
}

function PosterScore({
  card,
  side,
}: {
  card: RoundFeedCard;
  side: RoundFeedSide;
}) {
  const score = posterScore(card, side);
  return (
    <span className="fr-poster-score">
      <span className="fr-poster-score-note">
        {score.above && (
          <span>
            {score.above}
            {score.par ? " · " : ""}
          </span>
        )}
        {score.par && (
          <span
            className="fr-poster-par"
            aria-label={`${score.par} against course par`}
          >
            {score.par}
          </span>
        )}
      </span>
      <span>
        <b>{score.value}</b>
        <small>{score.label}</small>
      </span>
      {score.below && <small className="fr-poster-net">{score.below}</small>}
    </span>
  );
}
